import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const I18nContext = createContext(null);
const STORAGE_KEY = 'ui_language';
const DEFAULT_LANGUAGE = 'es';

const getValueByPath = (object, path) => {
  return String(path || '')
    .split('.')
    .reduce((acc, segment) => (acc && typeof acc === 'object' ? acc[segment] : undefined), object);
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE);

  const setLanguage = useCallback((nextLanguage) => {
    const normalized = nextLanguage === 'en' ? 'en' : 'es';
    setLanguageState(normalized);
    localStorage.setItem(STORAGE_KEY, normalized);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((translations, key, fallback = '') => {
    if (!translations || !key) return fallback;
    const selected = getValueByPath(translations?.[language], key);
    if (selected !== undefined) return selected;
    const spanish = getValueByPath(translations?.es, key);
    if (spanish !== undefined) return spanish;
    return fallback || key;
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
